/**
 * OHLCV-Features — alles, was sich aus Daily-Bars berechnen lässt:
 * - SMA über N Tage (z.B. MA200, MA50, MA20)
 * - MA-Slope: steigt der MA seit X Wochen?
 * - Position relativ zum MA (in %)
 * - VCP-Annäherung: enge Konsolidierung mit fallendem Range
 * - Donchian-Channel-Position
 * - ATR(N)
 *
 * Eingabe: chronologisch sortierte OhlcvBars (älteste zuerst).
 */

import type { OhlcvBar } from '@/lib/market-data/types';

export interface OhlcvFeatures {
  ma_long: number | null; // MA200 (oder MA-Periode aus Strategie)
  ma_long_slope_weeks: number | null; // wie viele Wochen steigt der MA200?
  /**
   * W94 — ECHTE MA200-Steigung in %/Woche (MA heute vs. MA vor 5 Bars).
   * Vorher gab es nur den Wochen-Zähler (immer ≥0); der daraus gebaute
   * Stufen-Proxy in build-features hatte ein Minimum von −0,1 — damit
   * waren "MA200 fällt"-KOs mathematisch unerreichbar. Bei Weekly-Bars
   * sind 5 Bars = 5 Wochen → Konsumenten mit %/Woche-Semantik nutzen
   * den Wert aus den DAILY-Features.
   */
  ma_long_slope_pct_per_week: number | null;
  pct_above_ma_long: number | null; // (close - ma_long) / ma_long * 100
  ma_mid: number | null; // MA50
  pct_above_ma_mid: number | null;
  donchian_high_55: number | null;
  donchian_position_pct: number | null; // (close - low55) / (high55 - low55) * 100
  atr14: number | null;
  // VCP-Approximation: Range-Contraction-Score 0-100
  vcp_contraction_score: number | null;
  vcp_reason: string;
  // L1 — Echtes VCP-Pattern (Minervini): mehrere abnehmende Pivot-Tiefen + schrumpfendes Volumen.
  /** 0-100 Score basierend auf Pivot-Folge + Volumen-Trend. null wenn nicht genug Bars. */
  vcp_pattern_score: number | null;
  vcp_pattern_reason: string;
  /** Anzahl detektierter Kontraktionen (Pivot-Tief-Folge). */
  vcp_contractions_detected: number | null;
  /** Tiefste Korrektur-Prozente je Kontraktion. */
  vcp_contractions_pct: number[] | null;
  // L4 — Anchored VWAP
  /** VWAP-Wert beim aktuellen Bar, verankert am tiefsten Tief der letzten 120 Bars. */
  anchored_vwap: number | null;
  /** Prozent-Abweichung Close vs. VWAP (positiv = über VWAP = bullish). */
  pct_above_vwap: number | null;
  /** Datum des Anchor-Bars (lowest low). */
  vwap_anchor_date: string | null;
  // ADX(14) — Average Directional Index
  adx14: number | null;
  /** +DI(14) — bullische Richtungskomponente */
  plus_di14: number | null;
  /** -DI(14) — bärische Richtungskomponente */
  minus_di14: number | null;
  // Audit V4: Volumen-/Liquiditäts-Features
  /** Durchschnittsvolumen letzte 20 Tage (Stück) */
  avg_volume_20: number | null;
  /** Durchschnittsvolumen letzte 50 Tage */
  avg_volume_50: number | null;
  /** Heutiges Volumen relativ zu avg_volume_20 — z.B. 1.5 = 50 % über Schnitt */
  volume_ratio: number | null;
  /** Avg-Dollar-Volumen letzte 20 Tage (close * volume) — Liquiditätsmaß */
  avg_dollar_volume_20: number | null;
  /** W47 (2026-05-17) — RSI(14) Wilder-Methode. 0-100, klassische
   *  Schwellenwerte 30 (ueberverkauft) / 70 (ueberkauft). */
  rsi14: number | null;
  /**
   * Welle 13 — Bollinger %B (20,2) auf Daily-Close-Basis.
   * %B = (close - lower) / (upper - lower). 0 = unteres Band, 1 = oberes Band,
   * >1 = über dem oberen Band (überkauft/überdehnt). Zentraler Überhitzungs-
   * Input fuer overextension.ts (Veto bei %B > 0.90). null wenn < 20 Bars
   * oder Band-Breite 0.
   */
  bb_pct_b: number | null;
  /**
   * Welle 13 — Reife des Moves: Anzahl Daily-Bars seit dem letzten echten
   * Pullback (≥8 % Rücksetzer vom laufenden Hoch). Hoher Wert = lang gelaufen
   * ohne Atempause → reifer/überdehnter Trend (Maturity-Penalty). null wenn
   * zu wenig Bars. Wird in build-features.ts in Wochen umgerechnet (÷5).
   */
  bars_since_last_pullback: number | null;
  /**
   * W67 Phase 3 (2026-05-24) — 52-Wochen-Hoch aus OHLCV-History.
   * Lookback: ~252 Trading-Days = 1 Kalenderjahr. Diese Berechnung ist die
   * SSoT fuer das 52W-Hoch — Provider-Quotes und AI-Felder sind nur noch
   * Fallback wenn weniger als 252 Bars verfuegbar sind.
   */
  high_52w_from_bars: number | null;
  /** W67 Phase 3 — analog 52-Wochen-Tief aus OHLCV-History. */
  low_52w_from_bars: number | null;
  /** W67 Phase 3 — Drawdown vom 52W-Hoch in % (negativ). Aus OHLCV-bars. */
  drawdown_from_52w_high_pct_from_bars: number | null;
  /**
   * W67 Phase 5b (2026-05-25) — 13/26/52-Wochen-Returns aus OHLCV-Bars.
   * Provider (TwelveData/Tiingo) liefert diese nicht, nur Finnhub. Fallback
   * auf OHLCV-derived stellt sicher, dass jedes Symbol mit ausreichenden
   * Bars (>= 65 fuer 13W) die Performance-Werte hat — auch bei stale Cache.
   */
  return_13w_pct_from_bars: number | null;
  return_26w_pct_from_bars: number | null;
  return_52w_pct_from_bars: number | null;
}

function sma(bars: OhlcvBar[], period: number, endIdx: number): number | null {
  if (endIdx + 1 < period) return null;
  let sum = 0;
  for (let i = endIdx - period + 1; i <= endIdx; i++) {
    const bar = bars[i];
    if (!bar) return null;
    sum += bar.close;
  }
  return sum / period;
}

function trueRange(prevClose: number, bar: OhlcvBar): number {
  return Math.max(
    bar.high - bar.low,
    Math.abs(bar.high - prevClose),
    Math.abs(bar.low - prevClose)
  );
}

/**
 * ATR nach WILDER (rekursive Glättung) — Audit 08.08.2026.
 *
 * BEFUND: Hier stand ein einfacher Mittelwert über n True Ranges. Das ist
 * nicht die ATR-Definition (Wilder 1978), sondern nur ihr Name. Im Repo lagen
 * dadurch ZWEI unvereinbare ATR-Varianten nebeneinander:
 *
 *   - Wilder    in src/lib/v6/setup-state.ts und src/lib/indicators.ts
 *   - Mittelwert hier — und diese Funktion speist den Score UND den
 *     Trade-Plan-Stop (src/lib/scoring/stop-math.ts)
 *
 * Das war kein akademischer Unterschied. An echten Kursen gemessen:
 *
 *   Symbol  Wilder  Mittelwert  Abweichung  Stop-Differenz
 *   MSFT    16,54   18,00       +8,9 %      6,61 je Aktie
 *   PYPL     1,66    1,51       −9,0 %      0,67
 *   GLD      7,53    6,91       −8,3 %      2,82
 *
 * Die App zeigte damit zwei verschiedene Ausstiegspreise für denselben Trade:
 * die Zustandsmaschinen-Zeile rechnete Wilder, die TradePlanCard daneben den
 * Mittelwert.
 *
 * Zusätzlich belegt das Signal-Konzept (§7, Kommentar in setup-state.ts), dass
 * die Variante das Ergebnis verschiebt: Dieselbe Regel lieferte mit einfachem
 * Mittelwert 51,3 % statt 59 % Treffer. Die Messung, auf der das ganze
 * Konzept steht, rechnet Wilder — also muss die Produktion es auch tun.
 */
function atr(bars: OhlcvBar[], period: number, endIdx: number): number | null {
  if (endIdx < period) return null;
  // Start: einfacher Mittelwert der ersten `period` True Ranges …
  let wert = 0;
  for (let i = 1; i <= period; i++) {
    const bar = bars[i];
    const prev = bars[i - 1];
    if (!bar || !prev) return null;
    wert += trueRange(prev.close, bar);
  }
  wert /= period;
  // … danach rekursiv geglättet bis zum Bewertungstag.
  for (let i = period + 1; i <= endIdx; i++) {
    const bar = bars[i];
    const prev = bars[i - 1];
    if (!bar || !prev) return null;
    wert = (wert * (period - 1) + trueRange(prev.close, bar)) / period;
  }
  return wert;
}

/**
 * ADX(14) — Average Directional Index nach Wilder.
 * Misst Trendstärke unabhängig von Richtung. ADX > 25 = starker Trend.
 * Berechnet auch +DI und -DI für Richtungsanalyse.
 *
 * Braucht mindestens 2×period Bars (28 für ADX14) für stabile Werte,
 * weil die erste Smoothing-Phase `period` Bars verbraucht.
 */
interface ADXResult {
  adx: number | null;
  plusDI: number | null;
  minusDI: number | null;
}

function computeADX(bars: OhlcvBar[], period: number = 14): ADXResult {
  const minBars = period * 2 + 1;
  if (bars.length < minBars) return { adx: null, plusDI: null, minusDI: null };

  const trArr: number[] = [];
  const plusDM: number[] = [];
  const minusDM: number[] = [];

  for (let i = 1; i < bars.length; i++) {
    const cur = bars[i]!;
    const prev = bars[i - 1]!;
    trArr.push(trueRange(prev.close, cur));

    const upMove = cur.high - prev.high;
    const downMove = prev.low - cur.low;
    plusDM.push(upMove > downMove && upMove > 0 ? upMove : 0);
    minusDM.push(downMove > upMove && downMove > 0 ? downMove : 0);
  }

  // Wilder-Smoothing: erste Periode = Summe, danach EMA-artig
  let smoothTR = 0;
  let smoothPlusDM = 0;
  let smoothMinusDM = 0;
  for (let i = 0; i < period; i++) {
    smoothTR += trArr[i]!;
    smoothPlusDM += plusDM[i]!;
    smoothMinusDM += minusDM[i]!;
  }

  const dxValues: number[] = [];
  for (let i = period; i < trArr.length; i++) {
    if (i > period) {
      smoothTR = smoothTR - smoothTR / period + trArr[i]!;
      smoothPlusDM = smoothPlusDM - smoothPlusDM / period + plusDM[i]!;
      smoothMinusDM = smoothMinusDM - smoothMinusDM / period + minusDM[i]!;
    }
    const pDI = smoothTR > 0 ? (smoothPlusDM / smoothTR) * 100 : 0;
    const mDI = smoothTR > 0 ? (smoothMinusDM / smoothTR) * 100 : 0;
    const diSum = pDI + mDI;
    const dx = diSum > 0 ? (Math.abs(pDI - mDI) / diSum) * 100 : 0;
    dxValues.push(dx);
  }

  if (dxValues.length < period) return { adx: null, plusDI: null, minusDI: null };

  // ADX = Wilder-Smoothed DX
  let adx = 0;
  for (let i = 0; i < period; i++) adx += dxValues[i]!;
  adx /= period;
  for (let i = period; i < dxValues.length; i++) {
    adx = (adx * (period - 1) + dxValues[i]!) / period;
  }

  // Letzte +DI / -DI
  const lastPDI = smoothTR > 0 ? (smoothPlusDM / smoothTR) * 100 : 0;
  const lastMDI = smoothTR > 0 ? (smoothMinusDM / smoothTR) * 100 : 0;

  return {
    adx: Math.round(adx * 100) / 100,
    plusDI: Math.round(lastPDI * 100) / 100,
    minusDI: Math.round(lastMDI * 100) / 100,
  };
}

/**
 * L1 — Echtes VCP-Pattern (Volatility Contraction Pattern, Minervini).
 *
 * Voraussetzungen für ein gültiges VCP:
 *   1. Aufwärtstrend der letzten Wochen (Pivot-Hochs steigend).
 *   2. 2-4 aufeinanderfolgende Korrekturen mit ABNEHMENDER Tiefe.
 *      (z.B. -25 % → -15 % → -8 %).
 *   3. Volumen schrumpft mit jeder Kontraktion.
 *   4. Konsolidierung über ≥ 4 Wochen.
 *
 * Wir detektieren Pivot-Hochs/Tiefs in einem 5-Tage-Fenster und prüfen
 * die letzten 6 Monate (~120 Bars). Score-Formel:
 *   - mind. 2 Kontraktionen mit abnehmender Tiefe → Basis 50
 *   - Bonus pro zusätzliche abnehmende Kontraktion: +15
 *   - Bonus wenn Volumen-Mittelwert sinkt: +20
 *   - Cap bei 100
 *
 * Liefert null wenn weniger als 60 Bars oder kein klares Pattern.
 */
interface VCPResult {
  score: number | null;
  reason: string;
  contractions: number | null;
  contractionsPct: number[] | null;
}

function detectVCPPattern(bars: OhlcvBar[], lastIdx: number): VCPResult {
  const minBars = 60;
  if (lastIdx + 1 < minBars) {
    return { score: null, reason: 'Zu wenig Bars für VCP-Mustererkennung (≥ 60 Tage nötig).', contractions: null, contractionsPct: null };
  }

  // Lookback: ~6 Monate = 120 Bars
  const startIdx = Math.max(0, lastIdx - 120);
  const window = bars.slice(startIdx, lastIdx + 1);

  // Pivot-Detection: lokales Hoch/Tief im 5-Tage-Fenster vor + nach
  const pivots: Array<{ idx: number; price: number; type: 'high' | 'low'; volume: number }> = [];
  const PIV = 5;
  for (let i = PIV; i < window.length - PIV; i++) {
    const cur = window[i];
    if (!cur) continue;
    let isHigh = true;
    let isLow = true;
    for (let j = i - PIV; j <= i + PIV; j++) {
      if (j === i) continue;
      const cmp = window[j];
      if (!cmp) continue;
      if (cmp.high >= cur.high) isHigh = false;
      if (cmp.low <= cur.low) isLow = false;
    }
    if (isHigh) pivots.push({ idx: i + startIdx, price: cur.high, type: 'high', volume: cur.volume });
    else if (isLow) pivots.push({ idx: i + startIdx, price: cur.low, type: 'low', volume: cur.volume });
  }

  if (pivots.length < 4) {
    return {
      score: null,
      reason: `Nur ${pivots.length} Pivot-Punkt(e) detektiert — kein klares VCP.`,
      contractions: null,
      contractionsPct: null,
    };
  }

  // Korrektur-Phasen: jedes Pivot-Tief nach einem Pivot-Hoch ist eine Kontraktion.
  // Wir ermitteln die prozentuale Korrektur (Hoch → Tief).
  const contractions: Array<{ pct: number; volumeAtHigh: number; volumeAtLow: number }> = [];
  for (let i = 0; i < pivots.length - 1; i++) {
    const a = pivots[i];
    const b = pivots[i + 1];
    if (!a || !b) continue;
    if (a.type === 'high' && b.type === 'low' && a.price > 0) {
      const pct = ((a.price - b.price) / a.price) * 100;
      if (pct > 1) {
        contractions.push({
          pct,
          volumeAtHigh: a.volume,
          volumeAtLow: b.volume,
        });
      }
    }
  }

  if (contractions.length < 2) {
    return {
      score: null,
      reason: `Nur ${contractions.length} Korrektur-Phase(n) — VCP braucht mind. 2 abnehmende Tiefen.`,
      contractions: contractions.length,
      contractionsPct: contractions.map((c) => c.pct),
    };
  }

  // Letzte 4 Kontraktionen: prüfen ob abnehmend
  const recent = contractions.slice(-4);
  let monotonicallyDecreasing = true;
  for (let i = 1; i < recent.length; i++) {
    if (recent[i]!.pct >= recent[i - 1]!.pct) {
      monotonicallyDecreasing = false;
      break;
    }
  }

  // Volumen-Trend: durchschnittliches Volumen pro Kontraktion sinkend?
  const volumeAvgs = recent.map((c) => (c.volumeAtHigh + c.volumeAtLow) / 2);
  let volumeShrinking = true;
  for (let i = 1; i < volumeAvgs.length; i++) {
    if (volumeAvgs[i]! >= volumeAvgs[i - 1]!) {
      volumeShrinking = false;
      break;
    }
  }

  // Score-Aufbau
  let score = 0;
  if (monotonicallyDecreasing) score += 50;
  else if (recent.length >= 2 && recent[recent.length - 1]!.pct < recent[0]!.pct) score += 25; // Tendenziell fallend

  // Bonus pro zusätzlich abnehmende Kontraktion
  if (monotonicallyDecreasing) score += Math.min(30, (recent.length - 2) * 15);

  // Volumen-Bonus
  if (volumeShrinking) score += 20;

  // Konsolidierungs-Dauer-Bonus: erste vs. letzte Pivot
  const firstIdx = pivots[0]!.idx;
  const lastIdxPiv = pivots[pivots.length - 1]!.idx;
  const consolidationBars = lastIdxPiv - firstIdx;
  if (consolidationBars >= 20) score += 5;
  if (consolidationBars >= 40) score += 5;

  score = Math.min(100, score);

  const contractionsPct = recent.map((c) => Math.round(c.pct * 10) / 10);
  let reason = `${recent.length} Kontraktionen detektiert: ${contractionsPct.map((p) => `${p}%`).join(' → ')}.`;
  if (monotonicallyDecreasing) reason += ' Streng abnehmend (Lehrbuch-VCP).';
  else reason += ' Nicht streng abnehmend — kein vollständiges VCP.';
  if (volumeShrinking) reason += ' Volumen schrumpft mit jeder Phase ✓';

  return {
    score,
    reason,
    contractions: recent.length,
    contractionsPct,
  };
}

export function computeOhlcvFeatures(
  bars: OhlcvBar[],
  options: { maLongPeriod?: number; maMidPeriod?: number; donchianPeriod?: number } = {}
): OhlcvFeatures {
  const maLongPeriod = options.maLongPeriod ?? 200;
  const maMidPeriod = options.maMidPeriod ?? 50;
  const donchianPeriod = options.donchianPeriod ?? 55;

  const last = bars.length - 1;
  if (last < 0) {
    return emptyFeatures();
  }
  const lastBar = bars[last];
  if (!lastBar) return emptyFeatures();
  const close = lastBar.close;

  const maLong = sma(bars, maLongPeriod, last);
  const maMid = sma(bars, maMidPeriod, last);

  // MA200-Slope: in wie vielen Wochen vor heute war der MA200 niedriger als heute?
  // Approximation: in 5/10/20 Tagen-Schritten zurück, prüfen ob MA200 jeweils niedriger war
  let slopeWeeks: number | null = null;
  if (maLong !== null) {
    let weeks = 0;
    for (let w = 1; w <= 12; w++) {
      const idx = last - w * 5;
      if (idx < maLongPeriod) break;
      const past = sma(bars, maLongPeriod, idx);
      if (past === null) break;
      if (past < maLong) {
        weeks = w;
      } else {
        break;
      }
    }
    slopeWeeks = weeks;
  }

  // Donchian-Channel-Position
  const donchianHigh = bars.slice(Math.max(0, last - donchianPeriod + 1), last + 1).reduce(
    (max, b) => Math.max(max, b.high),
    -Infinity
  );
  const donchianLow = bars.slice(Math.max(0, last - donchianPeriod + 1), last + 1).reduce(
    (min, b) => Math.min(min, b.low),
    Infinity
  );
  const donchianRange = donchianHigh - donchianLow;
  const donchianPosition =
    donchianRange > 0
      ? Math.max(0, Math.min(100, ((close - donchianLow) / donchianRange) * 100))
      : null;

  const atr14 = atr(bars, 14, last);
  const adxResult = computeADX(bars, 14);

  // VCP-Approximation: Letzte 6-10 Wochen, Range pro Woche soll abnehmen.
  let vcpScore: number | null = null;
  let vcpReason = 'Zu wenig Bars für VCP-Berechnung.';
  if (bars.length >= 50) {
    // Wir messen den Range (high-low) in 6 sequentiellen 5-Tage-Fenstern
    const ranges: number[] = [];
    for (let w = 6; w >= 1; w--) {
      const start = last - w * 5;
      const end = last - (w - 1) * 5;
      if (start < 0) continue;
      let high = -Infinity;
      let low = Infinity;
      for (let i = start; i <= end; i++) {
        const bar = bars[i];
        if (!bar) continue;
        high = Math.max(high, bar.high);
        low = Math.min(low, bar.low);
      }
      if (high > 0 && low > 0) {
        ranges.push(((high - low) / low) * 100);
      }
    }
    if (ranges.length >= 4) {
      const firstHalf = ranges.slice(0, Math.floor(ranges.length / 2));
      const secondHalf = ranges.slice(Math.floor(ranges.length / 2));
      const avgFirst = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
      const avgSecond = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
      const contraction = (avgFirst - avgSecond) / Math.max(avgFirst, 0.01);
      vcpScore = Math.max(0, Math.min(100, contraction * 200 + 50));
      // K6: Ehrlicher Klartext. Range-Verengung ist nur ein PROXY für VCP — echtes
      // VCP nach Minervini braucht 3-4 abnehmende Pivot-Tiefen mit fallendem Volumen.
      // Wir geben klar zu, dass wir das nicht messen.
      vcpReason = `Range hat sich um ${(contraction * 100).toFixed(0)} % verengt (von ${avgFirst.toFixed(1)} % auf ${avgSecond.toFixed(1)} %). Hinweis auf Volatilitäts-Kontraktion — kein vollständiges VCP-Pattern.`;
    }
  }

  // L1 — Echtes VCP-Pattern (Pivot-Folge + Volumen)
  const vcpPattern = detectVCPPattern(bars, last);

  // L4 — Anchored VWAP ab tiefstem Tief der letzten 120 Bars.
  // Klassisch: VWAP-Anker am bedeutsamen Swing-Low. Wenn Kurs über dem VWAP
  // liegt, sind die Käufer im Plus → bullisches Setup.
  let anchoredVwapValue: number | null = null;
  let pctAboveVwap: number | null = null;
  let vwapAnchorDate: string | null = null;
  if (bars.length >= 30) {
    const start = Math.max(0, last - 120);
    let lowestIdx = start;
    let lowest = bars[start]!.low;
    for (let i = start; i <= last; i++) {
      const b = bars[i];
      if (b && b.low < lowest) {
        lowest = b.low;
        lowestIdx = i;
      }
    }
    let cumPV = 0;
    let cumV = 0;
    for (let i = lowestIdx; i <= last; i++) {
      const b = bars[i];
      if (!b) continue;
      const typical = (b.high + b.low + b.close) / 3;
      cumPV += typical * b.volume;
      cumV += b.volume;
    }
    if (cumV > 0) {
      anchoredVwapValue = cumPV / cumV;
      pctAboveVwap = ((close - anchoredVwapValue) / anchoredVwapValue) * 100;
      vwapAnchorDate = bars[lowestIdx]?.timestamp.slice(0, 10) ?? null;
    }
  }

  // Audit V4: Volumen-/Liquiditäts-Features
  const vol20Bars = bars.slice(Math.max(0, last - 19), last + 1);
  const vol50Bars = bars.slice(Math.max(0, last - 49), last + 1);
  const avgVolume20 =
    vol20Bars.length === 20
      ? vol20Bars.reduce((s, b) => s + b.volume, 0) / 20
      : null;
  const avgVolume50 =
    vol50Bars.length === 50
      ? vol50Bars.reduce((s, b) => s + b.volume, 0) / 50
      : null;
  const todayVolume = bars[last]?.volume ?? 0;
  const volumeRatio =
    avgVolume20 !== null && avgVolume20 > 0 ? todayVolume / avgVolume20 : null;
  const avgDollarVolume20 =
    vol20Bars.length === 20
      ? vol20Bars.reduce((s, b) => s + b.close * b.volume, 0) / 20
      : null;

  // W47 — RSI(14) Wilder-Methode
  let rsi14: number | null = null;
  if (bars.length >= 15) {
    let avgGain = 0;
    let avgLoss = 0;
    for (let i = 1; i <= 14; i++) {
      const change = bars[i]!.close - bars[i - 1]!.close;
      if (change > 0) avgGain += change;
      else avgLoss -= change;
    }
    avgGain /= 14;
    avgLoss /= 14;
    for (let i = 15; i <= last; i++) {
      const change = bars[i]!.close - bars[i - 1]!.close;
      const gain = change > 0 ? change : 0;
      const loss = change < 0 ? -change : 0;
      avgGain = (avgGain * 13 + gain) / 14;
      avgLoss = (avgLoss * 13 + loss) / 14;
    }
    rsi14 = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }

  // Welle 13 — Bollinger %B (20,2) auf Daily-Close.
  // Oberes/unteres Band = SMA20 ± 2·StdDev(20, Population). %B verortet den
  // Close zwischen den Bändern. >0.90 = praktisch am oberen Band → überkauft,
  // zentraler Veto-Input fuer overextension.ts.
  let bbPctB: number | null = null;
  if (bars.length >= 20) {
    let sum = 0;
    for (let i = last - 19; i <= last; i++) sum += bars[i]!.close;
    const mean = sum / 20;
    let varSum = 0;
    for (let i = last - 19; i <= last; i++) {
      const d = bars[i]!.close - mean;
      varSum += d * d;
    }
    const sd = Math.sqrt(varSum / 20);
    const upper = mean + 2 * sd;
    const lower = mean - 2 * sd;
    const width = upper - lower;
    if (width > 0) bbPctB = (close - lower) / width;
  }

  // Welle 13 — Bars seit letztem echten Pullback (≥8 % Rücksetzer).
  // Wir laufen die letzten ≤252 Bars durch, tracken das laufende Hoch und
  // markieren jeden Bar, dessen Low ≥8 % unter dem bisherigen Hoch lag, als
  // Pullback. Der Abstand des letzten solchen Bars zum aktuellen Bar misst die
  // Reife des Moves. Kein Pullback in der ganzen Historie (≥20 Bars) → der
  // Move gilt als maximal reif (= Lookback-Länge).
  let barsSincePullback: number | null = null;
  const PULLBACK_PCT = 8;
  if (bars.length >= 20) {
    const lookback = Math.min(last, 252);
    const startIdx = last - lookback;
    let runningHigh = bars[startIdx]!.high;
    let lastPullbackIdx = -1;
    for (let i = startIdx; i <= last; i++) {
      const bar = bars[i];
      if (!bar) continue;
      if (runningHigh > 0 && ((bar.low - runningHigh) / runningHigh) * 100 <= -PULLBACK_PCT) {
        lastPullbackIdx = i;
      }
      if (bar.high > runningHigh) runningHigh = bar.high;
    }
    barsSincePullback = lastPullbackIdx >= 0 ? last - lastPullbackIdx : lookback;
  }

  // W67 Phase 3 — 52W-Hoch/Tief direkt aus den letzten ~252 Trading-Days.
  // Wenn weniger Bars da sind, nutzen wir alle vorhandenen (degradiert sauber
  // statt null zu liefern — z.B. junge IPOs mit nur 80 Bars zeigen ihr bisheriges
  // Allzeithoch). Provider/AI bleiben nur als Last-Resort-Fallback.
  const LOOKBACK_TRADING_DAYS = 252;
  const startIdx52W = Math.max(0, last - LOOKBACK_TRADING_DAYS + 1);
  let high52w = -Infinity;
  let low52w = Infinity;
  for (let i = startIdx52W; i <= last; i++) {
    const bar = bars[i];
    if (!bar) continue;
    if (bar.high > high52w) high52w = bar.high;
    if (bar.low < low52w) low52w = bar.low;
  }
  const high52wValid = Number.isFinite(high52w) && high52w > 0 ? high52w : null;
  const low52wValid = Number.isFinite(low52w) && low52w > 0 ? low52w : null;
  const drawdown52w =
    high52wValid !== null && close > 0 ? ((close - high52wValid) / high52wValid) * 100 : null;

  // W67 Phase 5b — 13/26/52-Wochen-Returns aus OHLCV-Bars.
  // Trading-Day-Approximationen: 13W ~65, 26W ~130, 52W ~252. Wenn weniger
  // Bars verfuegbar als Lookback verlangt, gibt's null (statt falscher Wert).
  function returnPct(daysBack: number): number | null {
    if (last < daysBack) return null;
    const past = bars[last - daysBack];
    if (!past || past.close <= 0) return null;
    return ((close - past.close) / past.close) * 100;
  }
  const return13w = returnPct(65);
  const return26w = returnPct(130);
  const return52w = returnPct(252);

  // W94 — echter Slope: MA(last) vs. MA(last−5 Bars), in %.
  const maLongPrev5 = sma(bars, maLongPeriod, last - 5);
  const maLongSlopePct =
    maLong !== null && maLongPrev5 !== null && maLongPrev5 > 0
      ? ((maLong - maLongPrev5) / maLongPrev5) * 100
      : null;

  return {
    ma_long: maLong,
    ma_long_slope_weeks: slopeWeeks,
    ma_long_slope_pct_per_week: maLongSlopePct,
    pct_above_ma_long: maLong !== null ? ((close - maLong) / maLong) * 100 : null,
    ma_mid: maMid,
    pct_above_ma_mid: maMid !== null ? ((close - maMid) / maMid) * 100 : null,
    donchian_high_55: donchianHigh > 0 && Number.isFinite(donchianHigh) ? donchianHigh : null,
    donchian_position_pct: donchianPosition,
    atr14,
    adx14: adxResult.adx,
    plus_di14: adxResult.plusDI,
    minus_di14: adxResult.minusDI,
    vcp_contraction_score: vcpScore,
    vcp_reason: vcpReason,
    vcp_pattern_score: vcpPattern.score,
    vcp_pattern_reason: vcpPattern.reason,
    vcp_contractions_detected: vcpPattern.contractions,
    vcp_contractions_pct: vcpPattern.contractionsPct,
    anchored_vwap: anchoredVwapValue,
    pct_above_vwap: pctAboveVwap,
    vwap_anchor_date: vwapAnchorDate,
    avg_volume_20: avgVolume20,
    avg_volume_50: avgVolume50,
    volume_ratio: volumeRatio,
    avg_dollar_volume_20: avgDollarVolume20,
    rsi14,
    bb_pct_b: bbPctB,
    bars_since_last_pullback: barsSincePullback,
    high_52w_from_bars: high52wValid,
    low_52w_from_bars: low52wValid,
    drawdown_from_52w_high_pct_from_bars: drawdown52w,
    return_13w_pct_from_bars: return13w,
    return_26w_pct_from_bars: return26w,
    return_52w_pct_from_bars: return52w,
  };
}

function emptyFeatures(): OhlcvFeatures {
  return {
    ma_long: null,
    ma_long_slope_weeks: null,
    ma_long_slope_pct_per_week: null,
    pct_above_ma_long: null,
    ma_mid: null,
    pct_above_ma_mid: null,
    donchian_high_55: null,
    donchian_position_pct: null,
    atr14: null,
    adx14: null,
    plus_di14: null,
    minus_di14: null,
    vcp_contraction_score: null,
    vcp_reason: 'Keine Bars.',
    vcp_pattern_score: null,
    vcp_pattern_reason: 'Keine Bars.',
    vcp_contractions_detected: null,
    vcp_contractions_pct: null,
    anchored_vwap: null,
    pct_above_vwap: null,
    vwap_anchor_date: null,
    avg_volume_20: null,
    avg_volume_50: null,
    volume_ratio: null,
    avg_dollar_volume_20: null,
    rsi14: null,
    bb_pct_b: null,
    bars_since_last_pullback: null,
    high_52w_from_bars: null,
    low_52w_from_bars: null,
    drawdown_from_52w_high_pct_from_bars: null,
    return_13w_pct_from_bars: null,
    return_26w_pct_from_bars: null,
    return_52w_pct_from_bars: null,
  };
}
